const post = {
  slug: 'small-docker-images',
  number: 3,
  title: 'Small Docker Images Are Worth the Effort',
  date: '2026-06-18',
  readTime: '6 min read',
  views: 72,
  category: 'devops',
  tags: ['Docker', 'DevOps', 'Platform'],
  excerpt:
    'For a long time I treated Docker images as something that just needed to work. Shrinking them turned out to be one of the cheapest wins available, and the payoff shows up on every single deploy.',
  cover: null,
  content: `
For a long time I treated Docker images the way most people do: as something that just needed to work. The image was over a gigabyte, the build took four minutes, and I told myself the size did not matter because it ran fine. Then I watched a deploy crawl during an incident, every node pulling that gigabyte image before it could start, and the size suddenly mattered a great deal. Big images slow down every deploy, every scale-up, and every cold start, and they quietly cost you money on storage and bandwidth the whole time.

Shrinking them turned out to be one of the cheapest, highest-return tasks available, and most of it is a handful of habits.

## Multi-stage builds do most of the work

The single trick that helped the most was the multi-stage build. You compile and install everything you need in one stage, then copy only the finished artifact into a clean, minimal final image. The build tools, the dev dependencies, the caches, none of it ships to production.

\`\`\`dockerfile
# Build stage — can be as messy as it needs to be
FROM node:20 AS build
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

# Runtime stage — only what's needed to run
FROM node:20-slim
WORKDIR /app
COPY --from=build /app/dist ./dist
COPY --from=build /app/node_modules ./node_modules
CMD ["node", "dist/server.js"]
\`\`\`

That one change took an image I was working with from well over a gigabyte down to a couple hundred megabytes, without touching a line of application code. The build stage can be as cluttered as it likes, because none of it survives into the image you actually ship.

## Order your layers by how often they change

Docker caches layers, so put the things that rarely change near the top and the things that change constantly near the bottom. Copy your lock file and install dependencies before you copy the rest of your source. Now a one-line code change does not throw away the entire dependency install, and your builds go from minutes to seconds.

This sounds like a minor detail and it is the difference between a build you wait on and a build that finishes before you have switched tabs. The cache is doing real work for you, and a badly ordered Dockerfile throws that work away on every single commit.

## A few small habits that add up

None of these are dramatic alone. Together they give you images that are smaller, faster, and safer:

- Use a slim or alpine base when your app allows it. The gap between a full base and its slim variant is often several hundred megabytes.
- Add a .dockerignore so you are not copying node_modules, .git, and local junk into the build context. A bloated context slows every build before the first instruction runs.
- Run as a non-root user. Two lines, and it closes off a whole class of container escape concerns.
- Pin the base image to a specific version instead of latest, so a rebuild months from now produces the same thing it does today.

## Smaller images are a security win too

There is a benefit beyond speed that is easy to overlook. Every package in your image is a potential vulnerability that a scanner will eventually flag. A minimal runtime image with only what your app needs has a far smaller attack surface than a full base carrying a compiler, a package manager, and a dozen utilities you never call.

So when you trim an image to its essentials, you are not just saving bandwidth. You are removing things that could show up in a security report six months from now and ruin an otherwise quiet afternoon. Smaller is safer, almost for free.

## The payoff

The reason any of this is worth an hour of your time is that the payoff lands on every deploy, which on a busy team is many times a day. A faster, smaller image means faster deploys, faster scaling, faster recovery during an incident, lower cost, and fewer vulnerabilities to triage. Spending an hour on your Dockerfile is one of the better trades in the whole delivery pipeline, and unlike most optimizations, you only have to do it once.
`,
};

export default post;
